<template>
  <details ref="detailsRef" class="cartDiscount">
    <summary class="cartDiscount__summary">
      <span class="cartDiscount__summary-label">Promo Code</span>
      <span class="cartDiscount__icon" aria-hidden="true">
        <svg class="cartDiscount__icon-plus" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path class="cartDiscount__icon-line cartDiscount__icon-line--horizontal" d="M7 2.75L7 11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
          <path class="cartDiscount__icon-line cartDiscount__icon-line--vertical" d="M2.75 7H11.25" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" />
        </svg>
      </span>
    </summary>

    <div class="cartDiscount__content">
      <div class="cartDiscount__form">
        <label class="visually-hidden" :for="inputId">Discount code</label>
        <input
          :id="inputId"
          v-model.trim="codeInput"
          class="cartDiscount__input"
          type="text"
          autocomplete="off"
          placeholder="Discount code"
          :disabled="isSubmitting"
          @keydown.enter.prevent="applyDiscount"
        />
        <button
          type="button"
          class="btn btn--white btn--small cartDiscount__apply"
          :disabled="isSubmitting || !codeInput"
          @click="applyDiscount"
        >
          Apply
        </button>
      </div>

      <div
        v-if="errorMessage"
        class="cartDiscount__error"
        role="alert"
      >
        {{ errorMessage }}
      </div>

      <ul v-if="appliedCodes.length" class="cartDiscount__codes" aria-label="Applied discounts">
        <li
          v-for="code in appliedCodes"
          :key="code"
          class="cartDiscount__pill"
        >
          <span class="cartDiscount__pill-code">{{ code }}</span>
          <button
            type="button"
            class="cartDiscount__pill-remove"
            :aria-label="`Remove discount ${code}`"
            :disabled="isSubmitting"
            @click="removeDiscount(code)"
          >
            <svg class="cartDiscount__pill-icon" aria-hidden="true">
              <use href="#close"></use>
            </svg>
          </button>
        </li>
      </ul>
    </div>
  </details>
</template>

<script setup>
  import { ref, computed, watch, onMounted } from 'vue';
  import { useStore } from 'vuex';

  const store = useStore();
  const detailsRef = ref(null);
  const inputId = `cart-discount-code-${Math.random().toString(36).slice(2, 9)}`;
  const codeInput = ref('');
  const errorMessage = ref('');
  const isSubmitting = ref(false);

  const appliedCodes = computed(() => store.getters.appliedDiscountCodes);
  const urlDiscountFeedback = computed(() => store.state.urlDiscountFeedback);

  const openAccordion = () => {
    if (detailsRef.value) {
      detailsRef.value.open = true;
    }
  };

  onMounted(() => {
    if (appliedCodes.value.length > 0) {
      openAccordion();
    }
  });

  watch(appliedCodes, (codes, previousCodes = []) => {
    if (codes.length > previousCodes.length) {
      openAccordion();
    }
  });

  watch(
    urlDiscountFeedback,
    (feedback) => {
      if (!feedback?.message) {
        return;
      }

      errorMessage.value = feedback.message;
      openAccordion();
    },
    { immediate: true }
  );

  // The URL notice is a one-shot for this page load, but it lives in shared
  // store state and the watcher above is immediate. Emptying and refilling the
  // cart unmounts and remounts this component, which would otherwise replay a
  // stale message. Clearing on any cart change closes that off, and runs long
  // after the notice is set on load.
  watch(
    () => store.state.cart.item_count,
    () => {
      if (store.state.urlDiscountFeedback) {
        store.commit('setUrlDiscountFeedback', null);
      }
    }
  );

  const applyDiscount = async () => {
    const code = codeInput.value.trim();
    if (!code || isSubmitting.value) return;

    errorMessage.value = '';
    isSubmitting.value = true;

    try {
      const result = await store.dispatch('applyDiscountCode', code);

      if (result?.alreadyApplied) {
        errorMessage.value = 'This discount code is already applied.';
        codeInput.value = '';
        openAccordion();
        return;
      }

      if (result?.error) {
        errorMessage.value = result.error;
        codeInput.value = '';
        openAccordion();
        return;
      }

      codeInput.value = '';
      openAccordion();
    } catch (error) {
      errorMessage.value = 'Unable to apply discount. Please try again.';
      openAccordion();
    } finally {
      isSubmitting.value = false;
    }
  };

  const removeDiscount = async (code) => {
    if (!code || isSubmitting.value) return;

    errorMessage.value = '';
    isSubmitting.value = true;

    try {
      const nextCodes = appliedCodes.value.filter(
        (c) => c.toLowerCase() !== code.toLowerCase()
      );
      const data = await store.dispatch('updateDiscount', nextCodes);

      if (!data) {
        errorMessage.value = 'Unable to remove discount. Please try again.';
      }
    } catch (error) {
      errorMessage.value = 'Unable to remove discount. Please try again.';
    } finally {
      isSubmitting.value = false;
    }
  };
</script>
